import { cloneDeep, get, groupBy, mapValues, merge, set, setWith, unset } from 'lodash';
import i18next from 'i18next';

/**
 * Filters the workflow rules results that have empty error messages:
 *    E.g.: { section: "basicFilingInfo", errorMessage: null, errorMessages: null, path: null, optional: false }
 * Transforms invidual workflow rules results into a section structured object for the validation panel:
 * {
 *    isValid: false,
 *    extraErrors: { },
 *    sectionValidations: {
 *      ...
 *      attachments: {
 *        percentage: 0,
 *        messages: ["The request type is required in order to select attachments"],
 *      },
 *    }
 * }
 */
export function processWorkflowValidations(workflowRulesResult) {
  const isValid = !workflowRulesResult?.some(rule => rule.errorMessage != null || rule.errorMessages?.length > 0);

  // maps results with multiple errors to multiple single error results
  const translatedValidations = workflowRulesResult?.flatMap((result) => {
    // multiple error messages inside the result
    if (result.errorMessages && result.errorMessages.length > 0) {
      const { errorMessages, ..._result } = result;
      return errorMessages.map((errorMessage) => {
        return {
          ..._result,
          errorMessage: i18next.t(errorMessage),
        };
      });
    }

    // single error message inside the result
    return {
      ...result,
      errorMessage: result.errorMessage ? i18next.t(result.errorMessage) : null,
    };
  });

  const translatedExtraErrors = translatedValidations?.filter(val => val.section === _extraPrefix);
  const validationsWithoutExtras = translatedValidations?.filter(val => val.section !== _extraPrefix);
  let extraErrors = {};
  if (translatedExtraErrors) {
    const foundExtraErrors = translatedExtraErrors.filter(x => x.errorMessage != null);
    extraErrors = _handleExtraErrors(foundExtraErrors, validationsWithoutExtras);
  }
  const validationsPerSection = groupBy(translatedValidations, i => i.section);
  const newValidations = mapValues(validationsPerSection, _percentageItem);

  return { isValid, extraErrors, sectionValidations: newValidations };
}

/**
 * Inject fields from rule validation results into the properties given by the validation.
 * @param {*} originalUiSchema
 * @param {*} schemaRules business rules in the format:
 * @returns uiSchema with validation fields injected dinamically
 */
export function populateUISchemaWithRulesResults(originalUiSchema, schemaRules) {
  if (originalUiSchema) {
    let uiSchema = cloneDeep(originalUiSchema);
    uiSchema = _cleanRuleAddedProperties(uiSchema);

    if (!schemaRules || Object.keys(schemaRules).length < 1) return uiSchema;

    schemaRules
      .filter(rule => !!rule)
      .forEach((rule) => {
        uiSchema = _mergeUISchemaWithRuleResults(uiSchema, rule);
      });
    return uiSchema;
  }
  return originalUiSchema;
}

/**
 * Removes all the properties added by business rule validations.
 * The rules have to be specified in a custom property like above:
 *    "addedByRule": ["ui:help", "ui:help:severity"]
 * @param {*} uiSchema
 * @returns cleaned up schema
 */
function _cleanRuleAddedProperties(uiSchema) {
  Object.keys(uiSchema).forEach((key) => {
    const field = uiSchema[key];
    if (field && typeof field === 'object') {
      if (field.addedByRule) {
        for (const property of field.addedByRule) {
          unset(field, property);
        }
        unset(field, 'addedByRule');
      }
      _cleanRuleAddedProperties(field);
    }
  });
  return uiSchema;
}

/**
 * Merges the rule properties with the ones of the field specified within the rule.
 * Uses the 'addedByRule' Set field to account for all the properties injected via rules.
 * @param {*} uiSchema input uiSchema
 * @param {*} rule validation rule
 * @returns uiSchema containing the input rule properties and the helper set property
 */
function _mergeUISchemaWithRuleResults(uiSchema, rule) {
  const propertyUiSchema = get(uiSchema, rule.property);
  const propertyUiSchemaMergedWithRule = merge(propertyUiSchema, rule);

  propertyUiSchemaMergedWithRule.addedByRule = propertyUiSchemaMergedWithRule.addedByRule || new Set();
  Object.keys(rule).forEach(key => propertyUiSchemaMergedWithRule.addedByRule.add(key));

  propertyUiSchemaMergedWithRule['ui:help'] = i18next.t(propertyUiSchemaMergedWithRule['ui:help']);
  return set(uiSchema, rule.property, propertyUiSchemaMergedWithRule);
}

const _percentageItem = (arr) => {
  let percentage = null;
  // optional means that the section is empty and optional, otherwise optional sections have false in optional field
  if (arr.find(i => !i.optional)) {
    percentage = (arr.filter(i => i.errorMessage == null).length / arr.length) * 100;
  }
  return {
    percentage,
    messages: arr.filter(i => i.errorMessage != null).map(i => i.errorMessage),
  };
};

function _setExtraErrorWithSpecificSection(extraErrors, validations, section) {
  if (extraErrors) {
    extraErrors.forEach((e) => {
      e.section = section;
    });
  }
}

const _extraPrefix = 'extra';
const _parties = 'parties';
const _attachments = 'attachments';
const _seqlBio = 'seqlBio';
const _fees = 'fees';
const _basicFilingInfo = 'basicFilingInfo';

function _mapExtraErrorToSpecificSection(extras, validationsWithoutExtras) {
  const extraPartiesErrors = extras.filter(extra => extra.path.startsWith(_parties));
  _setExtraErrorWithSpecificSection(extraPartiesErrors, validationsWithoutExtras, _parties);
  const extraAttachmentsErrors = extras.filter(extra => extra.path.startsWith(_attachments));
  _setExtraErrorWithSpecificSection(extraAttachmentsErrors, validationsWithoutExtras, _attachments);
  const extraSeqlErrors = extras.filter(extra => extra.path.startsWith(_seqlBio));
  _setExtraErrorWithSpecificSection(extraSeqlErrors, validationsWithoutExtras, _seqlBio);
  const extraFeesErrors = extras.filter(extra => extra.path.startsWith(_fees));
  _setExtraErrorWithSpecificSection(extraFeesErrors, validationsWithoutExtras, _fees);
  const extraBasicFilingInFoErrors = extras.filter(extra => extra.path.startsWith(_basicFilingInfo));
  _setExtraErrorWithSpecificSection(extraBasicFilingInFoErrors, validationsWithoutExtras, _basicFilingInfo);
}

function _setExtraErrorsOnInputField(extras, extraErrors) {
  extras.forEach((error) => {
    let errorsOnField = get(extraErrors, error.path);
    if (errorsOnField) {
      errorsOnField.__errors.push(error.errorMessage);
    } else {
      errorsOnField = { __errors: [error.errorMessage] };
    }
    setWith(extraErrors, error.path, errorsOnField, Object);
  });
}

function _handleExtraErrors(extras, validationsWithoutExtras) {
  const extraErrors = {};
  _mapExtraErrorToSpecificSection(extras, validationsWithoutExtras);
  _setExtraErrorsOnInputField(extras, extraErrors);
  return extraErrors;
}
